import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LegalPageShell from "../../components/LegalPageShell";

export default function RefundPolicy() {
  const { t } = useTranslation(["legalRefund", "common", "legal"]);

  const sections = [
    { id: "introduction", label: t("introduction", "Introduction") },
    { id: "current-payment-state", label: t("currentPaymentState", "Current Payment State") },
    { id: "refund-scope", label: t("refundScopeSection", "Refund Scope") },
    { id: "dispute-handling", label: t("disputeAndSupportHandling", "Dispute and Support Handling") },
    { id: "future-changes", label: t("futureChanges", "Future Changes") },
  ];

  return (
<LegalPageShell
        title={t("title", "Refund Policy")}
        subtitle={t("subtitle", "Orrith refund and dispute resolution policy.")}
        effectiveDate={`Effective: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`}
        sections={sections}
        namespace="legalRefund"
      >
      <section id="introduction" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("introduction", "Introduction")}
        </h2>
        <p className="legal-prose">
          {t("refundIntro", "This Refund Policy describes the current state of refunds and dispute resolution on Orrith at orrith.me. As Orrith's payment infrastructure evolves, this policy will be updated to reflect production payment processing terms.")}
        </p>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("atAGlance", "At a Glance")}
        </h3>
        <div className="grid gap-4 sm:grid-cols-3 mt-4 mb-8">
          <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 bg-gray-50 dark:bg-neutral-900/40">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-1.5">
              {t("currentStatus", "Current Status")}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("currentStatusDesc", "Current payment modal behavior is demo/testing and not a live settlement processor.")}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 bg-gray-50 dark:bg-neutral-900/40">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-1.5">
              {t("refundScope", "Refund Scope")}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("refundScopeDesc", "No production checkout refunds are processed by the current demo payment flow.")}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 bg-gray-50 dark:bg-neutral-900/40">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-1.5">
              {t("disputes", "Disputes")}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("disputesDesc", "Use Support Center tickets for offer or listing coordination disputes.")}
            </p>
          </div>
        </div>
      </section>

      <section id="current-payment-state" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("currentPaymentState", "Current Payment State")}
        </h2>
        <p className="legal-prose">
          {t("paymentStateDesc", "The current web payment modal in Orrith is a demo/testing flow. It simulates payment status for product experience validation and does not function as a production settlement processor for live card or wallet transactions. No actual funds are transferred through the current payment modal.")}
        </p>
      </section>

      <section id="refund-scope" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("refundScopeSection", "Refund Scope")}
        </h2>
        <p className="legal-prose mb-4">
          {t("refundScopeIntro", "Under the current demo payment implementation:")}
        </p>
        <ul className="legal-list">
          <li>{t("refundScopeItems.demoFlow", "No live in-app checkout settlement refunds are processed by the current demo payment modal")}</li>
          <li>{t("refundScopeItems.offerDisputes", "Offer disputes are coordination workflows, not live platform payment-collection workflows")}</li>
          <li>{t("refundScopeItems.futurePolicy", "Future live settlement policy terms will be published when production payment processing is enabled")}</li>
        </ul>

        <p className="legal-prose mt-6">
          {t("refundScopeNote", "When production payment processing is enabled, this section will be replaced with clear refund eligibility criteria, timelines (e.g., 14-day cooling-off period where applicable), processing rules, and exceptions.")}
        </p>
      </section>

      <section id="dispute-handling" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("disputeAndSupportHandling", "Dispute and Support Handling")}
        </h2>
        <p className="legal-prose mb-4">
          {t("disputeHandling.desc", "If you experience a transaction-related issue in listings or offers, use the Support Center ticket flow. Provide clear context so administrative reviewers can investigate and respond.")}
        </p>
        <p className="legal-prose mb-4">
          {t("disputeHandling.whatToInclude", "When submitting a dispute ticket, please include:")}
        </p>
        <ul className="legal-list">
          <li>{t("disputeHandling.ticketDetails", "Ticket details, timeline, and relevant screenshots when possible")}</li>
          <li>{t("disputeHandling.listingOfferId", "The listing or offer ID associated with the issue")}</li>
          <li>{t("disputeHandling.factual", "Factual descriptions to support accurate moderation review")}</li>
          <li>{t("disputeHandling.supportWorkflows", "Responses are handled through support workflows visible in your ticket history")}</li>
        </ul>

        <p className="legal-prose mt-6">
          {t("disputeHandlingProcess", "Administrative reviewers will assess the dispute based on available evidence, platform records, and applicable policies. Outcomes may include mediation, status correction, or escalation. There is no automated refund process in the current demo payment flow.")}
        </p>

        <Link
          to="/support"
          className="inline-flex mt-4 px-4 py-2 rounded-lg text-sm font-medium bg-green-600 hover:bg-green-700 text-white transition"
        >
          {t("openSupportCenter", "Open Support Center")}
        </Link>
      </section>

      <section id="future-changes" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("futureChanges", "Future Changes")}
        </h2>
        <p className="legal-prose mb-4">
          {t("futureChangesDesc", "If live payment settlement is enabled in production, this page will be updated with clear refund eligibility, timelines, and processing rules. The updated policy will address:")}
        </p>
        <ul className="legal-list">
          <li>{t("futureChangesItems.coolingOff", "Cooling-off periods and statutory refund rights where applicable")}</li>
          <li>{t("futureChangesItems.processingTimes", "Refund processing times by payment method")}</li>
          <li>{t("futureChangesItems.exceptions", "Exceptions (e.g., perishable goods, custom orders, digital content)")}</li>
          <li>{t("futureChangesItems.disputeResolution", "Formal dispute resolution and chargeback processes")}</li>
          <li>{t("futureChangesItems.notification", "Notification requirements for refund status changes")}</li>
        </ul>

        <p className="legal-prose mt-6">
          {t("futureChangesNotice", "We will provide reasonable notice of material changes to this policy. The effective date and version at the top of this page will reflect the currently active policy. For questions about refunds or disputes, contact us at legal@orrith.me.")}
        </p>
      </section>
    </LegalPageShell>
  );
}